import { Dialog, Transition } from '@headlessui/react';
import { ExchangeIcon, KlaviyoIcon } from 'components/icons';
import { ButtonCTASmall, SpinnerSmall } from 'components/ui';
import querystring from 'querystring';
import { Fragment, useRef, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { forEach } from 'lodash';
import {
  useGetStoreInfoQuery,
  useGetKlaviyoListQuery,
  useUpdateIntegrationMutation,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { LocationState } from '../types';
import { KlaviyoListIdSelector } from 'components/integrations/klaviyo';
import { KlaviyoListIdsSelection } from './types';

export const KlaviyoListImport = () => {
  const { query } = useParseURLQueryParams();

  const history = useHistory();

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const inputRef = useRef(null);

  const [klaviyoListIds, setKlaviyoListIds] = useState<KlaviyoListIdsSelection>(
    { selection: [] }
  );

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const [updateIntegration, { isLoading: isUpdateLoading }] =
    useUpdateIntegrationMutation();

  const location = useLocation<LocationState>();

  const handleCloseModal = () => {
    if (location?.state?.background) {
      history.push(location.state.background);
    } else {
      history.replace({
        ...history.location,
        pathname: '/integrations/connect',
      });
    }
  };

  const handleImport = () => {
    const selectedKlaviyoListIds: string[] = [];

    forEach(klaviyoListIds.selection, (item) => {
      if (item.selected) {
        selectedKlaviyoListIds.push(item.list_id);
      }
    });
    const payload = {
      signup_lists: selectedKlaviyoListIds,
    };
    updateIntegration({
      storeId: Number(storeInfo?.id),
      provider: 'klaviyo_lists',
      body: payload,
    })
      .unwrap()
      .then(handleCloseModal)
      .catch(() =>
        toast.error(
          'Fail to update the Klaviyo list, please try again later.',
          { toastId: 'api-error' }
        )
      );
  };

  const {
    data: klaviyoList,
    isSuccess: klaviyoListIsSuccess,
    isLoading: klaviyoListIsLoading,
  } = useGetKlaviyoListQuery(
    {
      provider: 'klaviyo',
      storeId: String(storeInfo?.id),
    },
    {
      skip: !!!storeInfo?.id,
    }
  );

  useEffect(() => {
    if (klaviyoListIsSuccess && klaviyoList) {
      setKlaviyoListIds({
        selection: klaviyoList.signup_lists,
      });
    }
  }, [klaviyoListIsSuccess, klaviyoList]);

  return (
    <Transition show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open
        initialFocus={inputRef}
        onClose={handleCloseModal}
      >
        <div className="min-h-screen text-center md:px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl md:h-segments-516px md:w-segments-648px">
              <div className="w-full h-48 px-5 pt-9 lg:px-12 bg-segments-accent-color-1 bg-opacity-5">
                <p className="text-black text-opacity-50 text-segments-13px">
                  Integrations
                </p>
                <div className="flex items-center mt-8 space-x-2 focus:outline-none">
                  <KlaviyoIcon />
                  <ExchangeIcon />
                  <p>{storeInfo?.name}</p>
                </div>
                <p className="mt-4 text-2xl">Import Klaviyo list</p>
              </div>
              <div className="flex flex-col justify-between px-5 pt-8 text-gray-700 lg:px-12 h-80">
                <div className="col-span-4 mt-4 md:mt-0">
                  <div>
                    {!!!klaviyoListIsLoading ? (
                      <KlaviyoListIdSelector
                        klaviyoListIds={klaviyoListIds}
                        setKlaviyoListIds={setKlaviyoListIds}
                      />
                    ) : (
                      <SpinnerSmall />
                    )}
                  </div>
                </div>
                <div className="flex justify-between mt-6 mb-6 ">
                  <div>
                    <ButtonCTASmall
                      type="button"
                      variant="secondary"
                      onClick={handleCloseModal}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Cancel
                      </p>
                    </ButtonCTASmall>
                  </div>
                  <div className="">
                    <ButtonCTASmall
                      variant="main"
                      type="button"
                      onClick={handleImport}
                      disabled={isUpdateLoading || !!!storeInfo?.id}
                    >
                      <p className="w-20 text-segments-13px">
                        {isUpdateLoading ? <SpinnerSmall /> : 'Submit'}
                      </p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
